/**
 * Fuentes del Brain — UN registro.
 *
 * Hasta el 30/09/2026 la lista de fuentes estaba escrita a mano en nueve lugares
 * (el union de `BrainPage.source`, las columnas por fuente de `adminStats`, la tab
 * Salud, la tarjeta de cita, el catálogo, los digests…): sumar una fuente era
 * tocar nueve archivos. Ahora una fuente es una entrada acá y el resto se deriva.
 *
 * Dos conceptos distintos:
 *  - `BRAIN_SYNCS`: las Lambdas que indexan (lo que la tab Salud puede disparar).
 *  - `BRAIN_SOURCES`: los valores de `brain_pages.source` (lo que se cita y se cuenta).
 *    Una fuente sabe qué sync la produce (`sync`), o es derivada (`sync: null`:
 *    highlights y sentiment las generan los crons de digest a partir de otras).
 *
 * Sin React ni texto de UI: el ícono es una clave (`icon`) que el front mapea a
 * lucide, el color es un tono (`tone`) que el front mapea a clases, y la etiqueta
 * vive en i18n (`brain.source.<id>`, `brain.sync.<id>`).
 */

export interface BrainSyncDef {
  fnEnv: string;
  /**
   * Cada cuánto corre su cron. `on-deploy` = markdown-sync, que no tiene cron (corre
   * en cada deploy a main): no se le puede exigir frescura diaria.
   */
  cadence: 'daily' | 'on-deploy';
  /**
   * Horas sin una corrida COMPLETA tras las cuales la fuente está muda.
   * `null` = no se vigila la frescura (cadencia irregular por diseño).
   *
   * Por qué dos umbrales y no uno: para un sync diario, 26 h significa "se salteó
   * la corrida de hoy" (puede ser un retraso de la cadena de self-invokes) y 36 h
   * significa "se salteó una corrida entera". El primero avisa en la UI, el segundo
   * dispara la alarma.
   */
  warnAfterHours:  number | null;
  staleAfterHours: number | null;
}

export const BRAIN_SYNCS = {
  slack:    { fnEnv: 'BRAIN_SLACK_SYNC_FN',    cadence: 'daily',     warnAfterHours: 26,   staleAfterHours: 36   },
  bq:       { fnEnv: 'BRAIN_BQ_SYNC_FN',       cadence: 'daily',     warnAfterHours: 26,   staleAfterHours: 36   },
  meet:     { fnEnv: 'BRAIN_MEET_SYNC_FN',     cadence: 'daily',     warnAfterHours: 26,   staleAfterHours: 36   },
  markdown: { fnEnv: 'BRAIN_MARKDOWN_SYNC_FN', cadence: 'on-deploy', warnAfterHours: null, staleAfterHours: null },
  /** HubSpot (30/09/2026): ficha comercial + deals por cliente, sobre el kit de conectores. */
  hubspot:  { fnEnv: 'BRAIN_HUBSPOT_SYNC_FN',  cadence: 'daily',     warnAfterHours: 26,   staleAfterHours: 36   },
  /** Gmail (19/10/2026, modo PML): hilos con la etiqueta del perfil en las casillas del perfil; sobre el kit. */
  gmail:    { fnEnv: 'BRAIN_GMAIL_SYNC_FN',    cadence: 'daily',     warnAfterHours: 26,   staleAfterHours: 36   },
  /** Google Chat (19/10/2026, modo PML): spaces del perfil, un thread por página; sobre el kit. */
  gchat:    { fnEnv: 'BRAIN_GCHAT_SYNC_FN',    cadence: 'daily',     warnAfterHours: 26,   staleAfterHours: 36   },
  /**
   * Calendar (19/10/2026, modo PML): reuniones por evento recurrente → notas de Gemini como páginas
   * `meet_transcript` (misma fuente que Meet; no tiene fuente propia en BRAIN_SOURCES).
   */
  calendar: { fnEnv: 'BRAIN_CALENDAR_SYNC_FN', cadence: 'daily',     warnAfterHours: 26,   staleAfterHours: 36   },
} as const satisfies Record<string, BrainSyncDef>;

export type BrainSyncId = keyof typeof BRAIN_SYNCS;
export const BRAIN_SYNC_IDS = Object.keys(BRAIN_SYNCS) as [BrainSyncId, ...BrainSyncId[]];
export const isBrainSyncId = (s: string): s is BrainSyncId =>
  Object.prototype.hasOwnProperty.call(BRAIN_SYNCS, s);

export type BrainSourceIcon = 'message-square' | 'video' | 'bar-chart' | 'star' | 'file-text' | 'sparkles' | 'heart' | 'briefcase' | 'mail' | 'message-circle';
export type BrainSourceTone = 'slack' | 'meet' | 'revenue' | 'nps' | 'docs' | 'derived' | 'crm' | 'gmail' | 'gchat';

export interface BrainSourceDef {
  /** Qué sync la produce; null = derivada de otras fuentes (digest) o escrita por otro camino. */
  sync: BrainSyncId | null;
  icon: BrainSourceIcon;
  tone: BrainSourceTone;
  /** Aparece en el desglose por fuente de la tab Salud (una card por cliente). */
  health: boolean;
  /** Material cualitativo: lo que leen los digests (highlights semanales, sentiment mensual). */
  qualitative: boolean;
  /**
   * Solo visible en modo PML (`pml:<cliente>.<bu>`): nunca entra al scope general de un cliente ni
   * a `all`, así un mail o un chat de una cuenta no aparece en la respuesta a cualquier Winclapper
   * (doc de Sol 19/10/2026). El filtro vive en lib/brain/search.ts (`sqlNotPmlOnly`).
   */
  pmlOnly?: boolean;
}

export const BRAIN_SOURCES = {
  slack:           { sync: 'slack',    icon: 'message-square', tone: 'slack',   health: true,  qualitative: true  },
  meet_transcript: { sync: 'meet',     icon: 'video',          tone: 'meet',    health: true,  qualitative: true  },
  bigquery:        { sync: 'bq',       icon: 'bar-chart',      tone: 'revenue', health: true,  qualitative: false },
  nps:             { sync: 'bq',       icon: 'star',           tone: 'nps',     health: false, qualitative: false },
  manual:          { sync: 'markdown', icon: 'file-text',      tone: 'docs',    health: true,  qualitative: false },
  /** Ficha comercial + deals de HubSpot (T2). No entra a los digests: la ficha cambia todos los días y no es "novedad". */
  hubspot:         { sync: 'hubspot',  icon: 'briefcase',      tone: 'crm',     health: true,  qualitative: false },
  /** Hilos de Gmail con la etiqueta del perfil (opt-in). Solo modo PML; fuera de los digests. */
  gmail:           { sync: 'gmail',    icon: 'mail',           tone: 'gmail',   health: true,  qualitative: false, pmlOnly: true },
  /** Threads de Google Chat de los spaces del perfil. Solo modo PML; fuera de los digests. */
  google_chat:     { sync: 'gchat',    icon: 'message-circle', tone: 'gchat',   health: true,  qualitative: false, pmlOnly: true },
  /** Resumen de la extensión de Meet (extension-webhook.ts); no entra a los digests para no cambiar su material. */
  brain_extension: { sync: null,       icon: 'video',          tone: 'meet',    health: false, qualitative: false },
  /** Digest semanal por cliente (WeeklyHighlightsCron). */
  highlights:      { sync: null,       icon: 'sparkles',       tone: 'derived', health: false, qualitative: false },
  /** Score mensual con drivers (SentimentBrainCron). */
  sentiment:       { sync: null,       icon: 'heart',          tone: 'derived', health: false, qualitative: false },
} as const satisfies Record<string, BrainSourceDef>;

export type BrainSourceId = keyof typeof BRAIN_SOURCES;
export const BRAIN_SOURCE_IDS = Object.keys(BRAIN_SOURCES) as BrainSourceId[];

export const isBrainSourceId = (s: string | null | undefined): s is BrainSourceId =>
  !!s && Object.prototype.hasOwnProperty.call(BRAIN_SOURCES, s);

/** Fuente desconocida (una fila vieja de Aurora, un valor nuevo sin registrar) ⇒ se muestra como "manual". */
export const brainSourceOrManual = (s: string | null | undefined): BrainSourceId =>
  isBrainSourceId(s) ? s : 'manual';

/** Columna de `adminStats` que cuenta las páginas de una fuente (`slack_pages`, `meet_transcript_pages`…). */
export const brainSourceStatsColumn = (id: BrainSourceId): string => `${id}_pages`;

const pick = (flag: keyof Pick<BrainSourceDef, 'health' | 'qualitative'>): BrainSourceId[] =>
  BRAIN_SOURCE_IDS.filter((id) => (BRAIN_SOURCES[id] as BrainSourceDef)[flag]);

/** Fuentes que la tab Salud desglosa por cliente. */
export const BRAIN_HEALTH_SOURCES: BrainSourceId[] = pick('health');
/** Fuentes cualitativas: el material de los digests (highlights, sentiment). */
export const BRAIN_QUALITATIVE_SOURCES: BrainSourceId[] = pick('qualitative');
/** Fuentes que SOLO se ven en modo PML (Gmail, Google Chat): el scope general y `all` las excluyen. */
export const BRAIN_PML_ONLY_SOURCES: BrainSourceId[] = BRAIN_SOURCE_IDS.filter(
  (id) => (BRAIN_SOURCES[id] as BrainSourceDef).pmlOnly === true
);

/**
 * Frescura de un sync: hace cuánto terminó su última corrida COMPLETA.
 *
 * Por qué no se mira `MAX(brain_pages.updated_at)` (incidente 05/10/2026): el upsert
 * de una página se saltea cuando el `content_hash` no cambió, así que una fuente cuyo
 * contenido no se movió (NPS, los markdown) tiene `updated_at` viejo aunque su sync
 * haya corrido hoy — y al revés, un sync MUERTO no se distingue de uno sin novedades.
 * Por eso la señal la publica el sync (métrica `RunCompleted`) y no el dato.
 *
 * Pura y sin reloj propio (`nowMs` entra por parámetro) para poder testearla.
 */
export type BrainSyncFreshness = 'ok' | 'warn' | 'stale' | 'unknown' | 'not-watched';

export function brainSyncFreshness(
  syncId: BrainSyncId,
  lastRunIso: string | null | undefined,
  nowMs: number
): BrainSyncFreshness {
  const def = BRAIN_SYNCS[syncId] as BrainSyncDef;
  if (def.staleAfterHours === null) return 'not-watched';
  if (!lastRunIso) return 'unknown';
  const ms = Date.parse(lastRunIso);
  if (!Number.isFinite(ms)) return 'unknown';
  const ageHours = (nowMs - ms) / 3_600_000;
  // Un reloj adelantado o una corrida "del futuro" no es un problema de frescura.
  if (ageHours < 0) return 'ok';
  if (ageHours >= def.staleAfterHours) return 'stale';
  if (def.warnAfterHours !== null && ageHours >= def.warnAfterHours) return 'warn';
  return 'ok';
}

/** Los syncs cuya frescura se vigila (alarma de CloudWatch + semáforo en la tab Salud). */
export const BRAIN_WATCHED_SYNCS: BrainSyncId[] = BRAIN_SYNC_IDS.filter(
  (id) => (BRAIN_SYNCS[id] as BrainSyncDef).staleAfterHours !== null
);
